import { getConfig } from './config';
import { getTransactionJson } from './solana-transaction-read';
import { attemptMirrorSync } from './operations';
const DEFAULT_TIMEOUT_MS = 60000;
const DEFAULT_MAX_ATTEMPTS = 20;
const DEFAULT_POLL_INTERVAL_MS = 3000;
/** Mirror read after a late confirmation. No settle delay: the tx already landed. */
const RECONCILE_MIRROR_SETTLE_MS = 0;
function errorText(error) {
    const message = error === null || error === void 0 ? void 0 : error.message;
    return typeof message === 'string' && message.length > 0 ? message : String(error);
}
function isSolanaSubmitted(receipt) {
    return receipt.status === 'submitted' && 'signedTransaction' in receipt;
}
/**
 * The Solana execution lane a client config REQUIRES, or `null` when the config
 * carries no constraint. Only a positive contradiction rejects a receipt — an
 * unmapped or unset `chain` is not evidence of a mismatch.
 */
function requiredSolanaChain(config) {
    switch (config.chain) {
        case 'realtime_devnet':
        case 'solana_devnet':
            return 'solana_devnet';
        case 'realtime_mainnet':
        case 'realtime_mainnet_preview':
        case 'solana_mainnet':
            return 'solana_mainnet';
        default:
            return null;
    }
}
/**
 * Bind the receipt to the app + chain it was issued for BEFORE any RPC or
 * mirror request. Writes bind to an immutable config, so a bare reconcile after
 * a global re-`init()` could otherwise poll the wrong app's endpoints entirely.
 */
function assertReceiptMatchesContext(receipt, config) {
    if (!receipt.appId || typeof receipt.appId !== 'string') {
        throw new Error('This write receipt carries no appId and cannot be reconciled.');
    }
    if (receipt.appId !== config.appId) {
        throw new Error(`This write receipt was issued for app "${receipt.appId}" but the bound client targets "${config.appId}". ` +
            'Reconcile it through the client it came from.');
    }
    if (isSolanaSubmitted(receipt)) {
        const required = requiredSolanaChain(config);
        if (required && required !== receipt.chain) {
            throw new Error(`This write receipt ran on "${receipt.chain}" but the bound client targets "${required}".`);
        }
    }
}
function stillSubmitted(receipt, reason) {
    return Object.assign(Object.assign({}, receipt), { confirmation: Object.assign(Object.assign({}, receipt.confirmation), { reason }) });
}
async function mirrorFor(receipt, options) {
    const paths = receipt.requestedDocuments.map(entry => entry.path);
    if (paths.length === 0)
        return { observed: false, reason: 'the receipt carried no requested paths' };
    const setOptions = (options === null || options === void 0 ? void 0 : options._overrides) ? { _overrides: options._overrides } : undefined;
    return attemptMirrorSync(paths, setOptions, RECONCILE_MIRROR_SETTLE_MS);
}
async function resolveSolanaRpc(config, options) {
    var _a, _b;
    const injected = (_a = options === null || options === void 0 ? void 0 : options._overrides) === null || _a === void 0 ? void 0 : _a._solanaRpc;
    if (injected)
        return injected;
    const rpcUrl = (_b = config.rpcUrl) === null || _b === void 0 ? void 0 : _b.trim();
    if (!rpcUrl) {
        throw new Error('Reconciling a Solana write receipt requires init({ rpcUrl }).');
    }
    const { Connection } = await import('@solana/web3.js');
    return new Connection(rpcUrl, 'confirmed');
}
/**
 * Solana reconciliation NEVER broadcasts. It reaches `expired_not_landed` only
 * after the fence is observed expired AND history successfully proves the
 * signature is absent; a failed or inconclusive lookup stays `submitted`.
 */
async function reconcileSolana(receipt, config, options) {
    var _a, _b, _c, _d;
    const rpc = await resolveSolanaRpc(config, options);
    let statusValue = null;
    let contextSlot;
    try {
        if (typeof rpc.getSignatureStatuses !== 'function') {
            return stillSubmitted(receipt, 'the configured Solana RPC cannot report signature statuses');
        }
        const statuses = await rpc.getSignatureStatuses([receipt.transactionId]);
        statusValue = (_b = (_a = statuses === null || statuses === void 0 ? void 0 : statuses.value) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : null;
        contextSlot = (_c = statuses === null || statuses === void 0 ? void 0 : statuses.context) === null || _c === void 0 ? void 0 : _c.slot;
    }
    catch (error) {
        return stillSubmitted(receipt, `signature status lookup failed: ${errorText(error)}`);
    }
    if (statusValue) {
        // Landed. A landed-but-failed transaction is a genuine failure, not `submitted`.
        if (statusValue.err) {
            throw new Error(`Transaction failed: ${JSON.stringify(statusValue.err)}`);
        }
        const confirmed = {
            status: 'confirmed',
            appId: receipt.appId,
            requestedDocuments: receipt.requestedDocuments,
            chain: receipt.chain,
            transactionId: receipt.transactionId,
            signedTransaction: receipt.signedTransaction,
            fence: receipt.fence,
            confirmationContextSlot: contextSlot !== null && contextSlot !== void 0 ? contextSlot : 0,
            mirrorSync: await mirrorFor(receipt, options),
        };
        return confirmed;
    }
    // Absent from the status window. Expiry has to be OBSERVED before absence can
    // mean anything: before the fence expires the transaction may still land.
    let blockHeight;
    try {
        blockHeight = typeof rpc.getBlockHeight === 'function' ? await rpc.getBlockHeight('confirmed') : undefined;
    }
    catch (error) {
        return stillSubmitted(receipt, `block height lookup failed: ${errorText(error)}`);
    }
    if (typeof blockHeight !== 'number') {
        return stillSubmitted(receipt, 'expiry could not be observed: the RPC reported no block height');
    }
    if (blockHeight <= receipt.fence.lastValidBlockHeight) {
        return stillSubmitted(receipt, `not yet expired: block height ${blockHeight} is within the fence (lastValidBlockHeight ${receipt.fence.lastValidBlockHeight})`);
    }
    // Expired. Prove absence from history before declaring the write retry-safe.
    let history;
    try {
        history = await getTransactionJson(rpc, receipt.transactionId, 'confirmed');
    }
    catch (error) {
        return stillSubmitted(receipt, `history lookup failed after expiry: ${errorText(error)}`);
    }
    if (history) {
        if ((_d = history === null || history === void 0 ? void 0 : history.meta) === null || _d === void 0 ? void 0 : _d.err) {
            throw new Error(`Transaction failed: ${JSON.stringify(history.meta.err)}`);
        }
        const confirmed = {
            status: 'confirmed',
            appId: receipt.appId,
            requestedDocuments: receipt.requestedDocuments,
            chain: receipt.chain,
            transactionId: receipt.transactionId,
            signedTransaction: receipt.signedTransaction,
            fence: receipt.fence,
            confirmationContextSlot: typeof history.slot === 'number' ? history.slot : (contextSlot !== null && contextSlot !== void 0 ? contextSlot : 0),
            mirrorSync: await mirrorFor(receipt, options),
        };
        return confirmed;
    }
    return {
        status: 'expired_not_landed',
        appId: receipt.appId,
        requestedDocuments: receipt.requestedDocuments,
        chain: receipt.chain,
        transactionId: receipt.transactionId,
        fence: receipt.fence,
    };
}
/**
 * Poll the SAME signature a `submitted` receipt carries and return the
 * resulting `SetResult`.
 *
 * - Terminal receipts (`committed`, `confirmed`, `expired_not_landed`,
 *   `signed`) are returned unchanged; nothing was left open.
 * - A `submitted` receipt with no transaction identifier (`transactionId:
 *   null`) is rejected immediately: no identifier exists to poll.
 * - A transaction that landed and FAILED throws, exactly as it would have on
 *   the original write.
 */
export async function reconcileSetResult(receipt, options) {
    var _a, _b;
    if (!receipt || typeof receipt !== 'object' || typeof receipt.status !== 'string') {
        throw new Error('reconcileSetResult requires a write receipt returned by set/setMany.');
    }
    if (receipt.status !== 'submitted')
        return receipt;
    if (receipt.transactionId === null) {
        throw new Error('This receipt is explicitly irreconcilable: the wallet send response was lost, so no transaction hash exists to poll. ' +
            'Resolve it out of band — resubmitting could execute the write twice.');
    }
    const config = (_b = (_a = options === null || options === void 0 ? void 0 : options._overrides) === null || _a === void 0 ? void 0 : _a._config) !== null && _b !== void 0 ? _b : await getConfig();
    // Validation happens BEFORE any RPC or mirror request.
    assertReceiptMatchesContext(receipt, config);
    if (isSolanaSubmitted(receipt)) {
        return reconcileSolana(receipt, config, options);
    }
    // Anything else (e.g. a legacy serialized receipt from a retired lane) has no
    // reconciliation transport any more; say so rather than guessing.
    return stillSubmitted(receipt, 'this receipt shape has no reconciliation lane');
}
/**
 * Reconcile repeatedly until the receipt leaves `submitted` or the bound is hit.
 *
 * The bound is mandatory, because some states are permanently non-terminal by
 * design: an RPC that can never observe the signature or the expiry never
 * resolves. On exhaustion (or abort) this returns the LATEST `submitted`
 * receipt with its identifier intact rather than hanging or throwing the
 * identifier away.
 */
export async function waitForSetResult(receipt, options) {
    var _a, _b, _c, _d;
    if (receipt && typeof receipt === 'object' && receipt.status === 'submitted' && receipt.transactionId === null) {
        // Rejected immediately rather than polled: there is nothing to poll.
        return reconcileSetResult(receipt, options);
    }
    const timeoutMs = (_a = options === null || options === void 0 ? void 0 : options.timeoutMs) !== null && _a !== void 0 ? _a : DEFAULT_TIMEOUT_MS;
    const maxAttempts = (_b = options === null || options === void 0 ? void 0 : options.maxAttempts) !== null && _b !== void 0 ? _b : DEFAULT_MAX_ATTEMPTS;
    const pollIntervalMs = (_c = options === null || options === void 0 ? void 0 : options.pollIntervalMs) !== null && _c !== void 0 ? _c : DEFAULT_POLL_INTERVAL_MS;
    const startedAt = Date.now();
    let current = receipt;
    let attempts = 0;
    while (attempts < maxAttempts && Date.now() - startedAt < timeoutMs) {
        if ((_d = options === null || options === void 0 ? void 0 : options.signal) === null || _d === void 0 ? void 0 : _d.aborted)
            return current;
        attempts++;
        current = await reconcileSetResult(current, options);
        if (current.status !== 'submitted')
            return current;
        if (attempts >= maxAttempts)
            break;
        const remaining = timeoutMs - (Date.now() - startedAt);
        if (remaining <= 0)
            break;
        const slept = await sleepUnlessAborted(Math.min(pollIntervalMs, remaining), options === null || options === void 0 ? void 0 : options.signal);
        if (!slept)
            return current;
    }
    return current;
}
/** Resolves `false` when the signal aborted during (or before) the sleep. */
function sleepUnlessAborted(ms, signal) {
    if (signal === null || signal === void 0 ? void 0 : signal.aborted)
        return Promise.resolve(false);
    if (!signal)
        return new Promise(resolve => setTimeout(() => resolve(true), ms));
    return new Promise(resolve => {
        const timer = setTimeout(() => {
            signal.removeEventListener('abort', onAbort);
            resolve(true);
        }, ms);
        const onAbort = () => {
            clearTimeout(timer);
            resolve(false);
        };
        signal.addEventListener('abort', onAbort, { once: true });
    });
}
